<template>
    <v-form ref="form" v-model="formValid">
        <v-row class="px-2">
            <v-col cols="12" sm="6" md="4">
                <v-autocomplete v-model="formData.parent_id" :label="$t('parentCategory')" :items="topCategories"
                    :disabled="!topCategories.length" item-text="name" item-value="id" hide-no-data clearable
                    color="teal lighten-2" item-color="teal lighten-2"></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" md="4">
                <v-autocomplete v-model="formData.status" :label="$t('status')" :items="statuses" item-text="name"
                    :disabled="!statuses.length" item-value="value" :rules="rules" hide-no-data clearable
                    color="teal lighten-2" item-color="teal lighten-2"></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" md="4">
                <v-autocomplete v-model="formData.redirect_code" label="Redirect code" :items="redirectCodes"
                    item-text="name" :disabled="!redirectCodes.length" item-value="value" hide-no-data clearable
                    color="teal lighten-2" item-color="teal lighten-2"></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" md="4" class="d-none">
                <v-text-field v-model="formData.system_name" :label="$t('alias')" :rules="rules" color="teal lighten-2"
                    item-color="teal lighten-2"></v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
                <v-text-field v-model="formData.name_ru" :label="$t('nameRu')" :rules="rules" color="teal lighten-2"
                    item-color="teal lighten-2"></v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
                <v-text-field v-model="formData.case_single_ru" :label="$t('caseSingleRu')" :rules="rules"
                    color="teal lighten-2" item-color="teal lighten-2"></v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
                <v-text-field v-model="formData.case_plural_ru" :label="$t('casePluralRu')" :rules="rules"
                    color="teal lighten-2" item-color="teal lighten-2"></v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
                <v-text-field v-model="formData.name_ua" :label="$t('nameUa')" :rules="rules" color="teal lighten-2"
                    item-color="teal lighten-2"></v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
                <v-text-field v-model="formData.case_single_ua" :label="$t('caseSingleUa')" :rules="rules"
                    color="teal lighten-2" item-color="teal lighten-2"></v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
                <v-text-field v-model="formData.case_plural_ua" :label="$t('casePluralUa')" :rules="rules"
                    color="teal lighten-2" item-color="teal lighten-2"></v-text-field>
            </v-col>

            <v-col cols="12" class="d-flex justify-end">
                <v-btn v-if="!isConfirmed" text color="teal" class="white--text mr-2" @click="confirmSubmit"
                    :disabled="!formValid">
                    {{ savingBtnText }}
                </v-btn>
                <v-btn v-if="isConfirmed" text color="teal" class="white--text mr-2" @click="save"
                    :disabled="!formValid || isLoading">
                    {{ confirmationBtnText }}
                </v-btn>
            </v-col>
        </v-row>
    </v-form>
</template>

<script>

import { CategoriesService } from '../../services'

const DEFAULT_CATEGORY = {
    id: null,
    parent_id: null,
    status: '',
    redirect_code: null,
    name_ru: '',
    case_single_ru: '',
    case_plural_ru: '',
    case_single_ua: '',
    case_plural_ua: '',
    name_ua: '',
    system_name: ''
};

export default {
    name: 'CategoryForm',
    props: {
        initialCategory: {
            type: Object,
            default: () => ({ ...DEFAULT_CATEGORY })
        },
        mode: {
            type: String,
            default: 'add'
        }
    },
    data() {
        return {
            isLoading: false,
            topCategories: [],
            statuses: [],
            redirectCodes: [],
            formData: { ...DEFAULT_CATEGORY },
            formValid: false,
            rules: [
                value => !!value || this.$t('requiredField')
            ],
            isConfirmed: false
        };
    },
    computed: {
        savingBtnText() {
            return this.$t('save');
        },
        confirmationBtnText() {
            if (this.mode === 'add') {
                return !this.formData.parent_id ? this.$t('addCategoryMain') : this.$t('addCategory');
            } else {
                return !this.formData.parent_id ? this.$t('updateCategoryMain') : this.$t('updateCategory');
            }
        },
        needRefresh() {
            return this.$store.state.updateCategoriesFilters;
        }
    },
    methods: {
        initMainCategories() {
            CategoriesService.getMainCategories().then(response => {
                this.topCategories = response.data;
            }).catch(error => {
                console.error('Error fetching main categories:', error);
            });
        },
        confirmSubmit() {
            if (this.$refs.form.validate()) {
                this.isConfirmed = true;
            }
        },
        save() {
            if (!this.$refs.form.validate()) {
                return;
            }

            if (this.mode === 'add') {
                this.create();
            } else if (this.mode === 'edit') {
                this.update();
            }
        },
        create() {
            this.isLoading = true;
            CategoriesService.create(this.formData)
                .then(response => {
                    this.submitted(response.data);
                })
                .catch(error => {
                    console.error('Error creating category:', error);
                }).finally(() => {
                    this.isLoading = false;
                });
        },
        update() {
            this.isLoading = true;
            CategoriesService.updateCategory(this.formData)
                .then(response => {
                    this.submitted(response.data);
                })
                .catch(error => {
                    console.error('Error updating category:', error);
                    this.failed(error);
                }).finally(() => {
                    this.isLoading = false;
                });
        },
        submitted(data) {
            this.$emit('submitted', data);
            this.isConfirmed = false;
        },
        failed(data) {
            this.$emit('failed', data);
            this.isConfirmed = false;
        }
    },
    created() {
        CategoriesService.setAuth(this.$store.getters.token);
        this.initMainCategories();
        CategoriesService.getStatuses().then(response => {
            this.statuses = response.data;
        }).catch(error => {
            console.error('Error fetching statuses:', error);
        });
        CategoriesService.getRedirectCodes().then(response => {
            this.redirectCodes = response.data;
        }).catch(error => {
            console.error('Error fetching redirect codes:', error);
        });
    },
    watch: {
        initialCategory: {
            immediate: true,
            handler(newValue) {
                if (newValue) {
                    this.formData = { ...newValue };
                } else {
                    this.formData = { ...DEFAULT_CATEGORY };
                }
                this.$nextTick(() => {
                    if (this.$refs.form) {
                        this.$refs.form.resetValidation();
                    }
                });
            }
        },
        needRefresh(value) {
            if (value) {
                this.initMainCategories();
                this.$store.commit('setUpdateCategoriesFilters', false);
            }
        }
    }
}
</script>
